import { useContext, useEffect } from 'react';
import { useHttp, useNotification } from 'src/common/hooks';
import { Circle } from 'src/common/types/circle';
import { AuthContext } from 'src/contexts';

export const DeleteCirclePopup = ({ circle, onDeleted, onClose }: DeleteCirclePopupProps) => {
  const notify = useNotification();
  const { accessToken } = useContext(AuthContext);

  const {
    sendRequest: deleteCircle,
    isLoading: isDeletingCircle,
    error: errorDeletingCircle,
  } = useHttp();

  useEffect(() => {
    if (errorDeletingCircle) {
      notify(errorDeletingCircle, 'error');
    }
  });

  const handleDeleteCircle = () => {
    deleteCircle(
      {
        url: `circle/${circle.id}`,
        method: 'DELETE',
        accessToken,
      },
      handleDeleteResponse,
    );
  };

  const handleDeleteResponse = () => {
    notify(`The “${circle.name}” circle was deleted.`, 'success');
    onDeleted();
    onClose();
  };

  return (
    <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-[#0B1444E5] bg-opacity-90 z-50">
      <div className="w-[600px] relative bg-white rounded-lg p-14 shadow-lg flex flex-col">
        <button className="absolute right-3 top-3" onClick={onClose}>
          <svg
            width="43"
            height="42"
            viewBox="0 0 43 42"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.5 15C0.5 6.71573 7.21573 0 15.5 0H27.5C35.7843 0 42.5 6.71573 42.5 15V27C42.5 35.2843 35.7843 42 27.5 42H15.5C7.21573 42 0.5 35.2843 0.5 27V15Z"
              fill="#E5E5E5"
            />
            <path d="M17.5 17L25.5 25" stroke="black" strokeWidth="2" strokeLinecap="round" />
            <path d="M25.5 17L17.5 25" stroke="black" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <h2 className="text-[30px] font-bold font-[sohne-breit] tracking-wide mb-5 text-center leading-[1] flex flex-wrap justify-center">
          Are you sure you want to <span className="mr-1.5 highlight-marker">delete the</span>
          <span className="truncate highlight-marker">“{circle.name}”</span>
          circle?
        </h2>

        <p className="text-black text-opacity-70 mb-[50px] text-center">
          You will loose the connection to all members in the circle.
        </p>

        <div className="flex gap-6 mx-auto">
          <button
            onClick={handleDeleteCircle}
            className="px-8 py-[21px] text-xs text-center font-medium font-[sohne-breit] tracking-wider text-white bg-[#C51C00] rounded-lg focus:outline-none hover:bg-opacity-90 transition-opacity"
            disabled={isDeletingCircle}
          >
            Yes, delete circle
          </button>

          <button
            onClick={onClose}
            disabled={isDeletingCircle}
            className="text-black underline text-opacity-70"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

interface DeleteCirclePopupProps {
  circle: Circle;
  onClose: () => void;
  onDeleted: () => void;
}
